import { Component, Input, Output, EventEmitter } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, ɵROUTER_PROVIDERS } from '@angular/router';
import { FaStackComponent } from '@fortawesome/angular-fontawesome';
import { CellClickedEvent, ColDef, ValueGetterParams, ValueFormatterParams, ICellRendererParams } from 'ag-grid-community'
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';
import 'ag-grid-enterprise';



/*
A companion for the work order wizard to get rates or pick a carrier
*/

@Component({
  selector: 'app-freightmancers-rate-friend',
  templateUrl: './freightmancers-rate-friend.component.html',
  styleUrls: ['./freightmancers-rate-friend.component.scss']
})
export class FreightmancersRateFriendComponent {
  // loading
  loading = false;
  result?: any;


  // for storing and evaluating input from freightmancer
  data: LadingData | null = null;
  errors: string[] = [];
  _isArgValid: boolean | null = null;

  @Input() set ladingInfo(value: LadingData | undefined){
    const result = validateLadingInfo(value);
    if (result.ok){
      this.data = result.value;
      this.errors = [];
      this._isArgValid = true;
    }
    else{
      this.data = null;
      this.errors = result.errors;
      this._isArgValid = false;
    }
  }

  @Output() carrierConfirmed = new EventEmitter<string>(); // a uuid

  raterUrl: string = '/api/rates/freightmancer_rates.php';

  // ag grid stuff - carrier options
  agTheme = 'ag-theme-ulaap';
  selectedCarrier: CarrierRate | null = null;
  carrierOptions: CarrierRate[] = [];
  carrierColumnDefs: ColDef[] = [
    { field: 'carrier_name', headerName: 'Carrier Name'},
    { field: 'rate_method', headerName: 'Rate Method'},
    { field: 'rate', headerName: 'Rate', type: 'numericColumn'},
    { field: 'min_rate', headerName: 'Min Rate', type: 'numericColumn'}, 
    { field: 'total_rate', headerName: 'Total Rate', type: 'numericColumn'},
    { headerName: 'Profit', colId: 'profit_margin',
      valueGetter: (params: ValueGetterParams<any>) => {
        const revenue = this.customerCharges?.total_rate ?? 0;
        const cost = params.data.total_rate ?? 0;
        if (revenue === 0) return 0;
        return ((revenue - cost) / revenue) * 100;
      },
      valueFormatter: (p: ValueFormatterParams<any>) => {
        const n = typeof p.value === 'number' ? p.value : Number(p.value ?? 0);
        return `${n.toFixed(2)}%`;
      },
      type: 'numericColumn'
    },
    {
      headerName: 'Select',
      colId: 'select',
      width: 110,
      sortable: false,
      filter: false,
      cellRenderer: () => '<button type="button" class="btn-select">Select</button>',
      cellStyle: { display: 'flex', justifyContent: 'center' }
    }
  ];
  defaultColDef: ColDef = {
    flex: 1,
    resizable: true,
    sortable: true,
    filter: true
  };

  // ag grid stuff - historic market rates
  historicRatesMarket: HistoricRate[] = [];
  historicRatesCustomer: HistoricRate[] = [];
  historicRatesCarrier: HistoricRate[] = [];
  historicRatesColumnDefs: ColDef[] = [
    { field: 'lading_id', headerName: 'Lading ID' },
    { field: 'origin_city', headerName: 'Origin City' },
    { field: 'origin_state', headerName: 'Origin State' },
    { field: 'dest_city', headerName: 'Destination City' },
    { field: 'dest_state', headerName: 'Destination State' },
    { field: 'total_charged', headerName: 'Total Charged', valueFormatter: params => Number(params.value).toLocaleString() },
    { field: 'gross_costs', headerName: 'Gross Costs', valueFormatter: params => Number(params.value).toLocaleString() },
    { field: 'created', headerName: 'Created',
       valueFormatter: params => {
        if (!params.value) return '';
        const date = new Date(params.value);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
    }
  ];
   historicMarketRatesColumnDefs: ColDef[] = [
    { field: 'lading_id', headerName: 'Lading ID' },
    { field: 'origin_city', headerName: 'Origin City' },
    { field: 'origin_state', headerName: 'Origin State' },
    { field: 'dest_city', headerName: 'Destination City' },
    { field: 'dest_state', headerName: 'Destination State' },
    { field: 'total_charged', headerName: 'Total Charged', valueFormatter: params => Number(params.value).toLocaleString() },
    { field: 'gross_costs', headerName: 'Gross Costs', valueFormatter: params => Number(params.value).toLocaleString() },
    { field: 'created', headerName: 'Created',
       valueFormatter: params => {
        if (!params.value) return '';
        const date = new Date(params.value);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
    },
    { field: 'lading_uuid', headerName: 'Open Lading', 
      cellRenderer: (params: ICellRendererParams) => {
        const a = document.createElement('a');
        a.href = `/work_order_details/${encodeURIComponent(params.value)}`;
        a.textContent = 'Open';
        a.target = '_blank';
        a.rel = 'noopener';
        a.className = 'ag-link-btn'; // optional class for styling
        return a;
      }
    }
  ];



  customerCharges: CustomerBillable | null = null;

  constructor(private route: ActivatedRoute, private http: HttpClient, private toastr: ToastrService){}

  ngOnInit() {
    if (this._isArgValid && this.data){
      this.loading = true;
      console.log(this.data)

      this.http.post<any>(this.raterUrl, this.data)
        .pipe(finalize(() => (this.loading = false)))
        .subscribe(
        (response) => {
          if(response.code && response.code !== '200'){
            this.toastr.error(response.message ?? 'Error getting data');
            return;
          }
          console.log('resp: ' + JSON.stringify(response));
          console.log('carrier rate: ' + response.carrier_rates[0].rate );
          // turn carrier amounts into dollars, and rate_method into actual human-readable info
          let carrierOptionsDollars: CarrierRate[] = response.carrier_rates;
          const rateMap: Record<string, string> = {
            '1': 'WEIGHT',
            '2': 'LINEAR FEET',
            '3': 'CUBIC WEIGHT',
            '4': 'WEIGHT PER CUBIC FEET'
          };
          const rateUnitMap: Record<string, string> = {
            '1': 'POUNDS',
            '2': 'KILOGRAMS',
            '3': 'FEET',
            '4': 'METERS'
          }

          for (let option of carrierOptionsDollars){
            option.rate = Math.round((option.rate / 100.00) * 100) / 100;
            option.total_rate /= 100.00;
            if (option.min_rate !== null)
              option.min_rate /= 100.00;
            option.rate_method = rateMap[response.rate_method] || 'WEIGHT';
          }
          console.log(carrierOptionsDollars)

          // turn customer amounts into dollars
          response.customer_billable.total_rate /= 100.00;
          response.customer_billable.rate /= 100.00;
          response.customer_billable.min_rate /= 100;
          response.customer_billable.rate_method = rateMap[response.customer_billable.rate_method];
          response.customer_billable.rate_unit = rateUnitMap[response.customer_billable.rate_unit];

          // set data for UI
          this.carrierOptions = carrierOptionsDollars;
          this.customerCharges = response.customer_billable;
          if(response.historic_rates_market && response.historic_rates_market.length > 0){
            this.historicRatesMarket = response.historic_rates_market;
          }
          if(response.historic_rates_customer && response.historic_rates.customer.length > 0){
            this.historicRatesCustomer = response.historic_rates_customer;
          }
        },
        (error) => {
          console.error(error)
          this.toastr.error('Error retrieving data');
        }
      )
    }
  }


  onCarrierCellClicked(event: CellClickedEvent){
    const target = event.event?.target as HTMLElement | null;
    if (event.column.getColId() === 'select' || target?.classList?.contains('btn-select')){
      this.selectedCarrier = event.data;
      console.log('selected carrier: ' + JSON.stringify(this.selectedCarrier))
    }
  }

  onCarrierConfirmed(){
    this.carrierConfirmed.emit(this.selectedCarrier?.carrier_contact_uuid ?? '');
  }

}


export interface Pallet{
  weight: number,
  length: number,
  width: number,
  height: number,
  number: number
}

export interface Zone{
  customer_zone_uuid: string,
  zone_name: string,
  zone_id: string
}

export interface CustomerBillable{
  rate: number,
  rate_uuid: string,
  rate_method: string,
  rate_unit: string,
  total_rate: number,
  min_rate: number
}

export interface CarrierRate{
  carrier_contact_uuid: string | null,
  carrier_name: string | null,
  carrier_zone_rate_uuid: string | null,
  rate_unit: string,
  rate_method: string,
  rate: number,
  min_rate: number | null,
  total_rate: number
}

export interface HistoricRate{
  lading_uuid: string,
  lading_id: string,
  origin_city: string,
  origin_state: string,
  dest_city: string,
  dest_state: string,
  total_charged: number,
  gross_costs: number,
  freight_net_billable: number | null,
  carrier_invoice_date: Date | null,
  created: Date | string,
  customer_contact_uuid: string,
  carrier_contact_uuid: string
}

export interface Response{
  measurement_used: {
    type: string,
    value: number,
    linear_ft: number,
    cubic_ft: number
  },
  zones: {
    origin: Zone,
    destination: Zone
  },
  customer_billable: CustomerBillable,
  carrier_rates: CarrierRate[],
  fsc_default: number | null,
  tax_rate: number,
  historic_rates_market: HistoricRate[],
  historic_rates_customer: any[],
  historic_rates_carrier: any[],
  historic_rates_customer_carrier: any[]
}

export interface LadingData{
    customer_contact_uuid: string;
    historic_search_radius_override?: number;
    rate_type: string;
    locations: {
      source: {
        type: string,
        value: string
      },
      destination: {
        type: string,
        value: string
      }
    };
    shipment: {
      pallets?: Pallet[],
      products?: any[];
    }
}

function validateLadingInfo(input: unknown):
| { ok: true; value: LadingData }
| { ok: false; errors: string[] }
{
  const errors: string[] = [];

  // helper functions
  const isNonEmptyStr = (v: any) => typeof v === 'string' && v.trim().length > 0;
  const isNum = (v: any) => typeof v === 'number' && Number.isFinite(v);

  if (typeof input !== 'object' || input === null){
    errors.push('Expected an object.');
    return {ok: false, errors};
  }
  const data = input as any;

  if(!isNonEmptyStr(data.customer_contact_uuid)){
    errors.push('Customer needs to be set.');
  }

  if(!isNonEmptyStr(data.rate_type)){
    errors.push('Rate type must be set.');
  }

  if(!data.locations || typeof data.locations !== 'object'){
    errors.push('Locations do not exist.');
  }
  else{
    const wordMapping: Record<string, string> = {
      source: 'Origin',
      destination: 'Destination'
    };
    ['source', 'destination'].forEach(key => {
      const location = data.locations[key];
      if (!location || typeof location !== 'object'){
        errors.push(`${key} is missing`);
      }
      else{
        if (!isNonEmptyStr(location.type)){
          errors.push(`$${wordMapping[key]} location type missing.`);
        }
        if (!isNonEmptyStr(location.value)){
          errors.push(`${wordMapping[key]} address missing.`);
        }
      }
    })
  }

  if(!data.shipment || typeof data.shipment !== 'object' || !Array.isArray(data.shipment.pallets)){
    errors.push('Pallets not an array.');
  }
  else if (data.shipment.pallets.length == 0){
    errors.push('At least one pallet is required.');
  }

  return errors.length ? {ok: false, errors: errors} : {ok: true, value: data as LadingData}
}

